import React from 'react';

const paginateColumn = (
  unitElements: JSX.Element[], // array of React elements to render the units
  unitDOMElements: Element[], // array of rendered DOM elements to measure
  columnIndex: number,
  numberOfColumns: number,
  firstColumnStartingPosition: number,
  firstColumnHeight: number,
  pageHeight: number,
  unitsByColumnAndPage: JSX.Element[][][] // result
): void => {
  let availableSpaceInPage = firstColumnHeight;
  let previousElTop = firstColumnStartingPosition;
  let page = 0;

  unitElements.map((unitElement, unitIndex): void => {
    const elBottom = unitDOMElements[unitIndex].getBoundingClientRect().bottom;
    const elHeight = elBottom - previousElTop; // not using boundingClientRect height cause we need to include margins
    previousElTop = elBottom;

    if (availableSpaceInPage > elHeight) {
      availableSpaceInPage -= elHeight;
    } else {
      availableSpaceInPage = pageHeight - elHeight;
      page++;
    }

    if (!unitsByColumnAndPage[page]) unitsByColumnAndPage[page] = [];
    for (let i = 0; i < numberOfColumns; i++) {
      if (!unitsByColumnAndPage[page][i]) unitsByColumnAndPage[page][i] = [];
    }

    unitsByColumnAndPage[page][columnIndex].push(unitElement);
  });
};

export const usePagination = (
  unitsByColumn: JSX.Element[][],
  firstPageRef: React.RefObject<HTMLDivElement>,
  firstColumnContainerRef: React.RefObject<HTMLDivElement>,
  setTotalPages?: (total: number) => void
): JSX.Element[][][] | undefined => {
  const lastRender = React.useRef<JSX.Element[][]>();
  const [pageVisible, setPageVisible] = React.useState<JSX.Element[][][]>();

  React.useLayoutEffect(() => {
    // first we render in a single page, then we can calculate
    if (lastRender.current !== unitsByColumn) {
      setPageVisible([unitsByColumn]);
      lastRender.current = unitsByColumn;
      return;
    }

    if (!firstPageRef.current || !firstColumnContainerRef.current) {
      return;
    }

    const unitsByPageAndColumn: JSX.Element[][][] = [];

    // calculate the available size of the columns (first page and others)
    const firstPageEl = firstPageRef.current;
    const firstColumnContainerEl = firstColumnContainerRef.current;
    const { top: templateStartingPosition, height: pageHeight } =
      firstPageEl.getBoundingClientRect();
    const firstColumnStartingPosition =
      firstColumnContainerRef.current.getBoundingClientRect().top;
    const firstColumnHeight =
      pageHeight - (firstColumnStartingPosition - templateStartingPosition);

    unitsByColumn.map((unitElements, columnIndex) => {
      const unitDOMElements: Element[] = Array.from(
        firstColumnContainerEl.children[columnIndex].children
      );

      paginateColumn(
        unitElements,
        unitDOMElements,
        columnIndex,
        unitsByColumn.length,
        firstColumnStartingPosition,
        firstColumnHeight,
        pageHeight,
        unitsByPageAndColumn
      );
    });

    setPageVisible(unitsByPageAndColumn);

    if (setTotalPages) {
      setTotalPages(
        typeof unitsByPageAndColumn !== 'undefined' &&
          unitsByPageAndColumn.length > 0
          ? unitsByPageAndColumn.length
          : 1
      );
    }
  }, [unitsByColumn, lastRender.current]);

  return pageVisible;
};
